// 15.D — список консультаций заказчика (вход из иконки-чата в шапке). Строка →
// тред. Непрочитанные ответы координатора помечены точкой/счётчиком.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, MessageCircle, Plus } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/Modal';
import { ConsultationThreadModal } from '@/components/ConsultationThreadModal';
import { AskSpecialistModal } from '@/components/AskSpecialistModal';

interface ConsultationRow {
  id: string; status: string; question: string; created_at: string;
  message_count: number; last_message_at: string | null; unread: number;
}
const C_STATUS: Record<string, string> = { new: 'Новая', quoted: 'Оценка стоимости', answered: 'Отвечено', closed: 'Закрыта' };

export function ConsultationsListModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [askOpen, setAskOpen] = useState(false);
  const { data, isLoading } = useQuery<ConsultationRow[]>({
    queryKey: ['my-consultations', projectId],
    queryFn: () => api.get<ConsultationRow[]>(`/projects/${projectId}/my-consultations`),
    refetchInterval: 15000,
  });
  const rows = data || [];

  if (openId) {
    return <ConsultationThreadModal projectId={projectId} consultationId={openId} onClose={() => setOpenId(null)} />;
  }
  if (askOpen) {
    return <AskSpecialistModal projectId={projectId} onClose={() => { setAskOpen(false); qc.invalidateQueries({ queryKey: ['my-consultations', projectId] }); }} />;
  }

  return (
    <Modal title="Консультации специалиста" onClose={onClose} maxWidth={560}>
      <button type="button" onClick={() => setAskOpen(true)}
        className="inline-flex items-center rounded-btn"
        style={{ marginBottom: 14, gap: 7, padding: '9px 16px', fontSize: 13.5, fontWeight: 600, background: '#0A1628', color: '#fff', border: 'none', cursor: 'pointer' }}>
        <Plus size={16} /> Задать вопрос специалисту
      </button>
      {isLoading ? (
        <p className="text-muted" style={{ fontSize: 13 }}>Загрузка…</p>
      ) : rows.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 13, textAlign: 'center', padding: '28px 16px', border: '1px dashed #E5E7EB', borderRadius: 10, lineHeight: 1.5 }}>
          <MessageCircle size={24} stroke="#9CA3AF" style={{ margin: '0 auto 8px' }} />
          Обращений пока нет. Задайте первый вопрос кнопкой выше — можно спросить и про систему, и по проекту.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((c) => (
            <button key={c.id} type="button" onClick={() => setOpenId(c.id)}
              className="text-left rounded-card"
              style={{ padding: '12px 14px', border: '1px solid #E5E7EB', background: c.unread > 0 ? 'rgba(226,75,74,.04)' : '#fff', cursor: 'pointer', display: 'block', width: '100%' }}>
              <div className="flex items-center" style={{ gap: 8 }}>
                {c.unread > 0 && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#E24B4A', flexShrink: 0 }} />}
                <span className="rounded-pill" style={{ padding: '2px 9px', fontSize: 10.5, fontWeight: 600, background: '#F3F4F6', color: '#374151' }}>
                  {C_STATUS[c.status] || c.status}
                </span>
                <span className="text-muted" style={{ fontSize: 11, marginLeft: 'auto' }}>
                  {c.unread > 0 ? <b style={{ color: '#B91C1C' }}>{c.unread} новых</b> : `${c.message_count} сообщ.`}
                </span>
                <ChevronRight size={15} stroke="#9CA3AF" />
              </div>
              <div className="text-ink" style={{ fontSize: 13, lineHeight: 1.45, marginTop: 6, fontWeight: c.unread > 0 ? 600 : 400 }}>
                {(c.question || '').slice(0, 120)}{(c.question || '').length > 120 ? '…' : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
